/**
 * Transaction Decoder
 *
 * Decodes a Kit `Transaction`'s compiled `messageBytes` into human-readable
 * instruction details using the INSTRUCTION_REGISTRY (Anchor IDLs) and
 * well-known programs (System, Token, ATA, Compute Budget).
 *
 * Kit's `getCompiledTransactionMessageDecoder` returns a structure that mirrors
 * web3.js's old `Message`/`MessageV0` but with Kit `Address` strings instead of
 * `PublicKey` objects.
 */

import {
  getCompiledTransactionMessageDecoder,
  type Address,
  type Transaction,
} from "@solana/kit";
import bs58 from "bs58";
import {
  lookupProgram,
  lookupInstruction,
  discriminatorToHex,
} from "@/lib/solana/instruction-registry";
import {
  lookupWellKnownProgram,
  lookupWellKnownInstruction,
} from "@/lib/solana/instruction-registry/well-known-programs";
import type { ArgField } from "@/lib/solana/instruction-registry";

export interface DecodedArg {
  name: string;
  label: string;
  value: string;
}

export interface DecodedAccount {
  pubkey: string;
  isSigner: boolean;
  isWritable: boolean;
}

export interface DecodedInstruction {
  index: number;
  programId: string;
  programName: string;
  instructionName: string;
  description: string;
  args: DecodedArg[];
  accounts: DecodedAccount[];
  isKnownProgram: boolean;
  rawData: string;
}

export interface DecodedTransaction {
  instructions: DecodedInstruction[];
  feePayer: string;
  recentBlockhash: string;
  warnings: string[];
}

/** u32 little-endian read at `offset`. Caller must ensure bounds. */
function readU32LE(data: Uint8Array, offset: number): number {
  return (
    (data[offset]! |
      (data[offset + 1]! << 8) |
      (data[offset + 2]! << 16) |
      (data[offset + 3]! << 24)) >>>
    0
  );
}

/** Raw 32 bytes → base58 Address string. */
function bytesToAddress(bytes: Uint8Array): string {
  return bs58.encode(bytes);
}

/**
 * Try to deserialize Anchor instruction args from data using ArgField layout.
 * Best-effort; returns partial results on malformed input.
 */
function tryDeserializeAnchorArgs(
  data: Uint8Array,
  offset: number,
  argFields: ArgField[],
): DecodedArg[] {
  const result: DecodedArg[] = [];
  let pos = offset;

  for (const field of argFields) {
    if (pos >= data.length) break;

    try {
      let value: string;
      const view = new DataView(data.buffer, data.byteOffset);

      if (field.optional) {
        if (pos >= data.length) break;
        const hasValue = data[pos]!;
        pos += 1;
        if (hasValue === 0) {
          result.push({ name: field.name, label: field.label, value: "None" });
          continue;
        }
      }

      switch (field.type) {
        case "u8":
          value = data[pos]!.toString();
          pos += 1;
          break;
        case "u16":
          value = view.getUint16(pos, true).toString();
          pos += 2;
          break;
        case "u32":
          value = view.getUint32(pos, true).toString();
          pos += 4;
          break;
        case "u64":
          value = view.getBigUint64(pos, true).toString();
          pos += 8;
          break;
        case "i64":
          value = view.getBigInt64(pos, true).toString();
          pos += 8;
          break;
        case "u128": {
          const lo = view.getBigUint64(pos, true);
          const hi = view.getBigUint64(pos + 8, true);
          value = ((hi << 64n) | lo).toString();
          pos += 16;
          break;
        }
        case "bool":
          value = data[pos] === 1 ? "true" : "false";
          pos += 1;
          break;
        case "pubkey":
          value = bytesToAddress(data.slice(pos, pos + 32));
          pos += 32;
          break;
        case "string": {
          const strLen = readU32LE(data, pos);
          pos += 4;
          value = new TextDecoder().decode(data.slice(pos, pos + strLen));
          pos += strLen;
          break;
        }
        case "bytes": {
          const bytesLen = readU32LE(data, pos);
          pos += 4;
          value = `[${bytesLen} bytes]`;
          pos += bytesLen;
          break;
        }
        default:
          value = "?";
      }

      result.push({ name: field.name, label: field.label, value });
    } catch {
      break;
    }
  }

  return result;
}

function tryDecodeAnchorInstruction(
  programId: string,
  data: Uint8Array,
): {
  programName: string;
  instructionName: string;
  description: string;
  args: DecodedArg[];
} | null {
  if (data.length < 8) return null;

  const programMeta = lookupProgram(programId);
  if (!programMeta) return null;

  const discriminatorBytes = Array.from(data.slice(0, 8));
  const discHex = discriminatorToHex(discriminatorBytes);
  const ixMeta = lookupInstruction(programId, discHex);
  if (!ixMeta) return null;

  const args = ixMeta.hasComplexArgs
    ? []
    : tryDeserializeAnchorArgs(data, 8, ixMeta.args);

  return {
    programName: programMeta.displayName,
    instructionName: ixMeta.displayName,
    description: ixMeta.description,
    args,
  };
}

function tryDecodeWellKnownInstruction(
  programId: string,
  data: Uint8Array,
): {
  programName: string;
  instructionName: string;
  description: string;
} | null {
  const program = lookupWellKnownProgram(programId);
  if (!program) return null;

  if (data.length < 1) {
    return {
      programName: program.displayName,
      instructionName: "Unknown",
      description: "",
    };
  }

  // Native programs use u8 or u32 LE as the instruction index.
  const ixIndex = data.length >= 4 ? readU32LE(data, 0) : data[0]!;
  const ixMeta = lookupWellKnownInstruction(programId, ixIndex);

  if (!ixMeta) {
    return {
      programName: program.displayName,
      instructionName: `Instruction #${ixIndex}`,
      description: "",
    };
  }

  return {
    programName: program.displayName,
    instructionName: ixMeta.displayName,
    description: ixMeta.description,
  };
}

/**
 * Decode a Kit `Transaction` into human-readable instruction details.
 *
 * Resolution order:
 *   1. Anchor INSTRUCTION_REGISTRY (programs with IDL)
 *   2. Well-known programs (System, Token, ATA, Compute Budget)
 *   3. Unknown program fallback
 */
export function decodeTransaction(tx: Transaction): DecodedTransaction {
  const msg = getCompiledTransactionMessageDecoder().decode(tx.messageBytes);
  const warnings: string[] = [];
  const instructions: DecodedInstruction[] = [];

  // Only legacy + v0 compiled messages carry a traditional `instructions`
  // array. V1 uses `instructionHeaders` / `instructionPayloads` and would need
  // a separate decode path — we emit a warning and skip for now.
  if (msg.version === 1) {
    return {
      instructions: [],
      feePayer: msg.staticAccounts[0] ? String(msg.staticAccounts[0]) : "Unknown",
      recentBlockhash: "hidden",
      warnings: ["V1 compiled transaction message — decoder not yet implemented"],
    };
  }

  const accountKeys: readonly Address[] = msg.staticAccounts;
  const feePayer = accountKeys.length > 0 ? String(accountKeys[0]!) : "Unknown";
  const header = msg.header;
  const totalAccounts = accountKeys.length;

  for (const ix of msg.instructions) {
    const programId = String(accountKeys[ix.programAddressIndex]!);
    const data: Uint8Array = ix.data ? Uint8Array.from(ix.data) : new Uint8Array(0);
    let rawData = "";
    for (let i = 0; i < data.length; i++) {
      rawData += data[i]!.toString(16).padStart(2, "0");
    }
    const ixIndex = instructions.length;

    const accountIndices: readonly number[] = ix.accountIndices ?? [];
    const accounts: DecodedAccount[] = accountIndices.map((keyIndex) => {
      const pubkey = String(accountKeys[keyIndex]!);
      const isSigner = keyIndex < header.numSignerAccounts;
      const isWritable =
        keyIndex <
          header.numSignerAccounts - header.numReadonlySignerAccounts ||
        (keyIndex >= header.numSignerAccounts &&
          keyIndex < totalAccounts - header.numReadonlyNonSignerAccounts);
      return { pubkey, isSigner, isWritable };
    });

    const anchorResult = tryDecodeAnchorInstruction(programId, data);
    if (anchorResult) {
      instructions.push({
        index: ixIndex,
        programId,
        programName: anchorResult.programName,
        instructionName: anchorResult.instructionName,
        description: anchorResult.description,
        args: anchorResult.args,
        accounts,
        isKnownProgram: true,
        rawData,
      });
      continue;
    }

    const wellKnownResult = tryDecodeWellKnownInstruction(programId, data);
    if (wellKnownResult) {
      instructions.push({
        index: ixIndex,
        programId,
        programName: wellKnownResult.programName,
        instructionName: wellKnownResult.instructionName,
        description: wellKnownResult.description,
        args: [],
        accounts,
        isKnownProgram: true,
        rawData,
      });
      continue;
    }

    const shortId = `${programId.slice(0, 4)}...${programId.slice(-4)}`;
    warnings.push(`Unknown program detected: ${programId}`);
    instructions.push({
      index: ixIndex,
      programId,
      programName: shortId,
      instructionName: `Unknown (0x${rawData.slice(0, 16)}...)`,
      description: "",
      args: [],
      accounts,
      isKnownProgram: false,
      rawData,
    });
  }

  return {
    instructions,
    feePayer,
    recentBlockhash: "hidden",
    warnings,
  };
}
